/**
 * Оформление графиков одним местом.
 *
 * Заведено 10.09.2026 при переходе на фирменный дизайн. До этого в каждом
 * графике стояли свои шестнадцатеричные цвета — шестьдесят штук по шести
 * файлам, — и у переключателя тёмной и светлой темы не было никакого способа на
 * них подействовать: график оставался тёмным на светлой странице.
 *
 * Поэтому цвета читаются из CSS-переменных ВО ВРЕМЯ отрисовки, а не берутся из
 * константы. Тема живёт в одном месте (`theme.css`), и график ей следует так же,
 * как карточка вокруг него.
 */

/** Значение переменной темы с корня документа. */
function token(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v === "" ? fallback : v;
}

/**
 * Категориальный ряд: фирменная последовательность логотипа — жёлтый, голубой,
 * розовый, фиолетовый, — дальше производные бренда. Порядок не случайный: он
 * задан брендбуком (с. 17), и графики читаются как часть фирменного стиля, а не
 * как случайный набор.
 */
export function series(): string[] {
  return [
    token("--s1", "#FFB600"),
    token("--s2", "#00C1DE"),
    token("--s3", "#ED40A9"),
    token("--s4", "#7E57C5"),
    token("--s5", "#FF8300"),
    token("--s6", "#0071CE"),
    token("--s7", "#22D3E8"),
    token("--s8", "#C6B098"),
  ];
}

export type ChartTheme = {
  ink: string;
  ink2: string;
  ink3: string;
  line: string;
  line2: string;
  card: string;
  card2: string;
  card3: string;
  pos: string;
  neg: string;
  warn: string;
  accent: string;
  series: string[];
  /** Ось: подписи, линия, засечки. Раскладывается в опции echarts. */
  axis: Record<string, unknown>;
  /** Всплывающая подсказка в цветах карточки, а не по умолчанию. */
  tooltip: Record<string, unknown>;
  /** Разлиновка по значению: тонкая, чтобы не спорить с данными. */
  splitLine: Record<string, unknown>;
  font: string;
};

export function chartTheme(): ChartTheme {
  const ink = token("--ink", "#EEF4FB");
  const ink2 = token("--ink-2", "#A3BCDA");
  const ink3 = token("--ink-3", "#7192B9");
  const line = token("--line", "rgba(255,255,255,.11)");
  const line2 = token("--line-2", "rgba(255,255,255,.22)");
  const card = token("--card", "#0A2547");
  const font = token("--f-body", '"Alegreya Sans", system-ui, sans-serif');

  return {
    ink,
    ink2,
    ink3,
    line,
    line2,
    card,
    card2: token("--card-2", "#0D2C53"),
    card3: token("--card-3", "#123663"),
    pos: token("--pos", "#22D3E8"),
    neg: token("--neg", "#FF6BC0"),
    warn: token("--warn", "#FFB600"),
    accent: token("--accent", "#FFB600"),
    series: series(),
    font,
    axis: {
      axisLine: { lineStyle: { color: line2 } },
      axisTick: { show: false },
      axisLabel: { color: ink3, fontSize: 11, fontFamily: font },
    },
    tooltip: {
      backgroundColor: card,
      borderColor: line2,
      borderWidth: 1,
      textStyle: { color: ink, fontFamily: font, fontSize: 12 },
      extraCssText: "border-radius:9px; box-shadow:2px 2px 0 rgba(0,0,0,.25)",
    },
    splitLine: { lineStyle: { color: line } },
  };
}
